import * as React from 'react';
import { Radio, RadioGroup } from '@components/base/inputs';
import type { RadioGroupOption } from '@components/base/inputs';
import { VStack } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

const sizes = ['sm', 'md', 'lg'] as const;

const policyTermOptions: RadioGroupOption[] = [
  { value: '6mo', label: '6 Months' },
  { value: '12mo', label: '12 Months' },
  { value: '24mo', label: '24 Months' },
  { value: '36mo', label: '36 Months', disabled: true },
];

const paymentOptions: RadioGroupOption[] = [
  { value: 'monthly', label: 'Monthly Payment' },
  { value: 'quarterly', label: 'Quarterly Payment' },
  { value: 'annually', label: 'Annual Payment (Save 10%)' },
];

const deductibleOptions: RadioGroupOption[] = [
  { value: '250', label: '$250 Deductible' },
  { value: '500', label: '$500 Deductible' },
  { value: '1000', label: '$1,000 Deductible' },
  { value: '2500', label: '$2,500 Deductible' },
];

function RadioShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  const [selectedTerm, setSelectedTerm] = React.useState<string>('12mo');
  const [selectedPayment, setSelectedPayment] = React.useState<string>('');
  const [selectedDeductible, setSelectedDeductible] = React.useState<string>('500');

  const orientation = playgroundProps.orientation as 'horizontal' | 'vertical';

  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="radio group">
          <div className="max-w-md">
            <RadioGroup
              name="preview-term"
              label="Policy Term"
              options={policyTermOptions}
              size={playgroundProps.size as 'sm' | 'md' | 'lg'}
              disabled={playgroundProps.disabled as boolean}
              orientation={orientation}
              value={selectedTerm}
              onChange={setSelectedTerm}
            />
          </div>
      </ShowcasePreviewSection>

      {/* Size Variants */}
      <ShowcaseDemoSection
        title="Size Variants"
        description="Radio buttons come in three sizes: small (16px), medium (18px), and large (22px)"
       
      >
          <VStack gap={6}>
            {sizes.map((size) => (
              <div key={size}>
                <RadioGroup
                  name={`size-demo-${size}`}
                  label={`${size.toUpperCase()} Size`}
                  options={[
                    { value: 'opt1', label: 'Option 1' },
                    { value: 'opt2', label: 'Option 2' },
                  ]}
                  size={size}
                  orientation="horizontal"
                />
              </div>
            ))}
          </VStack>
      </ShowcaseDemoSection>

      {/* Individual Radios */}
      <ShowcaseDemoSection
        title="Individual Radio Buttons"
        description="Radio buttons can be used individually with shared name attribute"
       
      >
          <VStack gap={3}>
            <Radio name="individual-demo" label="First option" value="first" />
            <Radio name="individual-demo" label="Second option" value="second" defaultChecked />
            <Radio name="individual-demo" label="Third option" value="third" />
          </VStack>
      </ShowcaseDemoSection>

      {/* Vertical Orientation */}
      <ShowcaseDemoSection
        title="Vertical Layout"
        description="Default vertical layout for radio groups"
       
      >
          <div className="max-w-md">
            <RadioGroup
              name="payment-vertical"
              label="Payment Schedule"
              options={paymentOptions}
              value={selectedPayment}
              onChange={setSelectedPayment}
              helperText="Choose how often you would like to pay"
            />
          </div>
      </ShowcaseDemoSection>

      {/* Horizontal Orientation */}
      <ShowcaseDemoSection
        title="Horizontal Layout"
        description="Radio groups can be displayed horizontally"
       
      >
          <RadioGroup
            name="coverage-horizontal"
            label="Coverage Level"
            options={[
              { value: 'basic', label: 'Basic' },
              { value: 'standard', label: 'Standard' },
              { value: 'premium', label: 'Premium' },
            ]}
            orientation="horizontal"
          />
      </ShowcaseDemoSection>

      {/* With Helper Text */}
      <ShowcaseDemoSection
        title="With Helper Text"
        description="Radio groups with additional context"
       
      >
          <div className="max-w-md">
            <RadioGroup
              name="deductible"
              label="Select Deductible"
              options={deductibleOptions}
              value={selectedDeductible}
              onChange={setSelectedDeductible}
              helperText="Higher deductibles result in lower monthly premiums"
            />
          </div>
      </ShowcaseDemoSection>

      {/* Required */}
      <ShowcaseDemoSection
        title="Required Selection"
        description="Radio groups that require a selection"
       
      >
          <div className="max-w-md">
            <RadioGroup
              name="required-coverage"
              label="Primary Driver"
              options={[
                { value: 'self', label: 'Myself' },
                { value: 'spouse', label: 'Spouse/Partner' },
                { value: 'child', label: 'Child' },
                { value: 'other', label: 'Other' },
              ]}
              required
            />
          </div>
      </ShowcaseDemoSection>

      {/* Error State */}
      <ShowcaseDemoSection
        title="Error State"
        description="Radio groups with validation errors"
       
      >
          <div className="max-w-md">
            <RadioGroup
              name="error-demo"
              label="Claim Type"
              options={[
                { value: 'collision', label: 'Collision' },
                { value: 'comprehensive', label: 'Comprehensive' },
                { value: 'liability', label: 'Liability' },
              ]}
              required
              error
              errorMessage="Please select a claim type to continue"
            />
          </div>
      </ShowcaseDemoSection>

      {/* Disabled Options */}
      <ShowcaseDemoSection
        title="Disabled Options"
        description="Individual options can be disabled within the group"
       
      >
          <div className="max-w-md">
            <RadioGroup
              name="term-disabled"
              label="Policy Term"
              options={policyTermOptions}
              helperText="36 month terms are currently unavailable"
            />
          </div>
      </ShowcaseDemoSection>

      {/* Disabled Group */}
      <ShowcaseDemoSection
        title="Disabled Group"
        description="Entire radio group can be disabled"
       
      >
          <div className="max-w-md">
            <RadioGroup
              name="disabled-group"
              label="Locked Selection"
              options={paymentOptions}
              value="annually"
              disabled
              helperText="Payment schedule cannot be changed for this policy"
            />
          </div>
      </ShowcaseDemoSection>

      {/* Individual Radio States */}
      <ShowcaseDemoSection
        title="Individual Radio States"
        description="Various states for individual radio buttons"
       
      >
          <VStack gap={4}>
            <Radio name="states-demo-1" label="Default unchecked" value="default" />
            <Radio name="states-demo-2" label="Checked state" value="checked" defaultChecked />
            <Radio name="states-demo-3" label="Disabled unchecked" value="disabled" disabled />
            <Radio name="states-demo-4" label="Disabled checked" value="disabled-checked" defaultChecked disabled />
            <Radio name="states-demo-5" label="Error state" value="error" error helperText="This selection has an error" />
          </VStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const RadioShowcase: ShowcaseDefinition = {
  id: 'radio',
  name: 'Radio',
  category: 'inputs',
  description: 'Single selection from multiple options',
  propsDefinition: [
    {
      name: 'size',
      type: 'select',
      options: ['sm', 'md', 'lg'],
      defaultValue: 'md',
      description: 'Size of the radio buttons (sm=16px, md=18px, lg=22px)',
    },
    {
      name: 'disabled',
      type: 'boolean',
      defaultValue: false,
      description: 'Whether the radio group is disabled',
    },
    {
      name: 'orientation',
      type: 'select',
      options: ['horizontal', 'vertical'],
      defaultValue: 'vertical',
      description: 'Layout orientation of the radio buttons',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'role="radiogroup" on container',
      'Native radio role on inputs',
      'aria-checked (via native checked)',
      'aria-disabled (via native disabled)',
      'aria-invalid (error state)',
      'aria-describedby (helper/error text)',
    ],
    keyboardNav: [
      'Tab: Move focus to/from radio group',
      'Arrow keys: Move selection between options',
      'Space: Select focused option',
    ],
    notes: [
      'Uses native radio inputs with custom styling',
      'Only one option can be selected per group',
      'Arrow keys cycle through options',
      'Focus ring visible on keyboard navigation',
    ],
  },
  component: RadioShowcaseComponent,
};
